import { data, type HeadersFunction, type LoaderFunctionArgs } from "react-router";
import { useLoaderData } from "react-router";
import { QueryClientProvider } from "@tanstack/react-query";
import invariant from "invariant";
import { client } from "~/client/client";
import { PublicCalendar } from "~/features/calendar/public/PublicCalendar";
import { queryClient } from "~/utils/queryClient";
import { getBaseParams } from "~/utils/getBaseParams";

export const headers: HeadersFunction = () => ({
  "X-Frame-Options": "ALLOWALL",
  "Content-Security-Policy": "frame-ancestors * file: data:",
  "Access-Control-Allow-Origin": "*",
});

export async function loader({ request }: LoaderFunctionArgs) {
  const url = new URL(request.url);
  const code = url.searchParams.get("code") || undefined;
  const appointmentTypeGuid = url.searchParams.get("appointmentTypeGuid") || undefined;

  let partnerLanding;
  let appointmentTypes;

  if (code) {
    const baseParams = await getBaseParams(request, { withDeviceGuid: false });
    const { data: responseData, error } = await client.v1.getApiPartnerLandingByCode({
      ...baseParams,
      code,
    });

    if (error) {
      throw new Response(null, { status: 400, statusText: error.error });
    }

    partnerLanding = responseData?.response;

    if (partnerLanding) {
      const appointmentTypesResponse = await client.v1.getApiPartnerAppointmentTypeList(partnerLanding.partner_guid, {
        client_bundle_id: baseParams.client_bundle_id,
        device_locale: baseParams.device_locale,
      });

      appointmentTypes = appointmentTypesResponse.data?.response?.partner_appointment_types;
    }
  }

  if (appointmentTypeGuid) {
    invariant(code, "code is required when appointmentTypeGuid is set");
  }

  return data({
    code,
    appointmentTypeGuid,
    partnerLanding,
    appointmentTypes,
  });
}

export default function CalendarEmbed() {
  const { appointmentTypeGuid, partnerLanding, appointmentTypes } = useLoaderData<typeof loader>();

  return (
    <QueryClientProvider client={queryClient}>
      <div className="min-h-screen bg-white p-4">
        <PublicCalendar
          partnerLanding={partnerLanding}
          initialAppointmentTypes={appointmentTypes}
          appointmentType={
            appointmentTypeGuid
              ? ({ partner_appointment_type_guid: appointmentTypeGuid, name: "", description: "", duration: 0 } as any)
              : undefined
          }
        />
      </div>
    </QueryClientProvider>
  );
}
